import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErroApi } from '../lib/api';
import {
  criarQuadro,
  enviarArquivo,
  listarQuadros,
  removerQuadro,
  salvarQuadro,
  type QuadroResumo,
} from '../lib/quadro';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import Layout from '../componentes/Layout';

export default function Quadros() {
  const [quadros, setQuadros] = useState<QuadroResumo[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [enviandoCapa, setEnviandoCapa] = useState<number | null>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const d = await listarQuadros();
      setQuadros(d.quadros);
      setPodeEditar(d.podeEditar);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os quadros.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function trocarCapa(quadro: QuadroResumo, arquivo: File) {
    setErro(null);
    setEnviandoCapa(quadro.id);
    try {
      const enviado = await enviarArquivo(arquivo);
      await salvarQuadro(quadro.id, { capaCaminho: enviado.caminho });
      await carregar();
    } catch (e) {
      // ARMADILHA 12: falha de upload aparece, nao some
      setErro(e instanceof Error ? e.message : 'Não consegui enviar a capa.');
    } finally {
      setEnviandoCapa(null);
    }
  }

  return (
    <Layout
      titulo="Quadros"
      subtitulo="Escolha o quadro para abrir as demandas dele."
      acoes={podeEditar && <Botao onClick={() => setCriando(true)}>+ Novo quadro</Botao>}
    >
      {erro && (
        <div className="mb-4 max-w-xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {quadros.map((q) => (
          <div key={q.id} className="overflow-hidden rounded-2xl bg-white shadow-cartao">
            <Link to={`/quadro/${q.chave}`} className="block">
              {q.capaCaminho ? (
                <img
                  src={`/api/uploads/${q.capaCaminho}`}
                  alt=""
                  loading="lazy"
                  className="h-40 w-full object-cover"
                />
              ) : (
                <span className="flex h-40 w-full items-center justify-center bg-tinta-900 text-3xl font-bold uppercase text-white">
                  {q.nome.trim().slice(0, 2)}
                </span>
              )}
              <span className="block px-4 pt-3">
                <span className="block font-semibold text-tinta-900">{q.nome}</span>
                <span className="block text-xs text-tinta-400">
                  {q.cards} {q.cards === 1 ? 'card' : 'cards'}
                </span>
              </span>
            </Link>

            {podeEditar && (
              <div className="flex items-center gap-3 px-4 pb-3 pt-2 text-xs">
                <label className="cursor-pointer font-medium text-tinta-900 hover:underline">
                  {enviandoCapa === q.id ? 'enviando...' : q.capaCaminho ? 'Trocar capa' : 'Capa'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      const arquivo = e.target.files?.[0];
                      if (arquivo) void trocarCapa(q, arquivo);
                      e.target.value = '';
                    }}
                  />
                </label>
                {q.capaCaminho && (
                  <>
                    <a
                      href={`/api/uploads/${q.capaCaminho}`}
                      download
                      className="font-medium text-tinta-900 hover:underline"
                    >
                      ↓ Baixar capa
                    </a>
                    <button
                      onClick={async () => {
                        await salvarQuadro(q.id, { capaCaminho: null });
                        await carregar();
                      }}
                      className="text-tinta-400 hover:text-tinta-900"
                    >
                      remover capa
                    </button>
                  </>
                )}
                <button
                  onClick={async () => {
                    const ok = await confirmar({
                      titulo: `Tirar "${q.nome}" da lista?`,
                      texto: 'So sai se estiver sem cards. O histórico continua guardado.',
                      confirmar: 'Tirar da lista',
                    });
                    if (!ok) return;
                    try {
                      await removerQuadro(q.id);
                      await carregar();
                    } catch (e) {
                      setErro(e instanceof ErroApi ? e.message : 'Não consegui remover.');
                    }
                  }}
                  className="ml-auto text-tinta-400 hover:text-red-600"
                >
                  remover
                </button>
              </div>
            )}
          </div>
        ))}

        {podeEditar && (
          <button
            onClick={() => setCriando(true)}
            className="flex min-h-[13rem] items-center justify-center rounded-2xl border border-dashed border-tinta-200 text-sm text-tinta-400 transition hover:border-tinta-900 hover:text-tinta-900"
          >
            + Adicionar quadro
          </button>
        )}
      </div>

      {criando && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-tinta-900/30 p-4"
          onClick={() => setCriando(false)}
        >
          <div className="my-auto w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <Cartao>
              <h3 className="text-lg font-semibold text-tinta-900">Novo quadro</h3>
              <input
                autoFocus
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && nome.trim()) {
                    try {
                      await criarQuadro(nome.trim());
                      setNome('');
                      setCriando(false);
                      await carregar();
                    } catch (erroCriar) {
                      setErro(erroCriar instanceof ErroApi ? erroCriar.message : 'Não consegui criar.');
                    }
                  }
                }}
                placeholder="Ex.: Louvor, Redes sociais, Eventos"
                className="mt-3 w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
              />
              <p className="mt-2 text-xs text-tinta-400">
                O quadro ja nasce com as colunas de sempre, inclusive Prontos, Agendamentos e Entregue.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <Botao variante="secundario" onClick={() => setCriando(false)}>
                  Cancelar
                </Botao>
                <Botao
                  onClick={async () => {
                    if (!nome.trim()) return;
                    try {
                      await criarQuadro(nome.trim());
                      setNome('');
                      setCriando(false);
                      await carregar();
                    } catch (e) {
                      setErro(e instanceof ErroApi ? e.message : 'Não consegui criar.');
                    }
                  }}
                >
                  Criar quadro
                </Botao>
              </div>
            </Cartao>
          </div>
        </div>
      )}
      {dialogo}
    </Layout>
  );
}
